import {useEffect, useMemo, useState} from 'react';
import {Layout} from '@/components/Layout';
import {Button} from '@/components/ui/button';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {Textarea} from '@/components/ui/textarea';
import {toast} from 'sonner';
import {Edit2, Megaphone, Palette, Plus, Search, Shapes, ShieldOff, Trash2} from 'lucide-react';
import {usePermissions} from '@/hooks/usePermissions';
import {
    api,
    getApiErrorMessage,
    massAlertTemplateEndpoints,
    objectColorEndpoints,
    objectIconEndpoints,
} from '@/lib/api';
import {PERMISSIONS} from '@/lib/permissions';

const emptyForm = {
    code: '',
    name: '',
    object_color_id: '',
    object_icon_id: '',
    default_subject: '',
    default_message: '',
    order_no: '0',
    status: '1',
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const idFrom = (source) => source?.id ?? source?.object_color_id ?? source?.object_icon_id ?? '';

const normalizeOption = (source = {}) => {
    const id = idFrom(source);
    const label = source.label || source.name || source.title || String(id);
    return {
        ...source,
        id,
        name: label,
        label,
        color: source.color || source.hex || source.hex_color || source.value || '',
        icon: source.icon || source.icon_name || source.name || '',
    };
};

const ColorOption = ({color}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        <span
            className="w-3 h-3 rounded-full border border-slate-200 flex-shrink-0"
            style={{backgroundColor: color?.color || '#64748b'}}
        />
        <span className="truncate">{color?.label || color?.name || 'Sem nome'}</span>
    </span>
);

const IconOption = ({icon}) => (
    <span className="inline-flex items-center gap-2 min-w-0">
        {icon?.icon ? (
            <img src={icon.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0"/>
        ) : (
            <Shapes size={14} className="text-slate-500 flex-shrink-0"/>
        )}
        <span className="truncate">{icon?.label || icon?.name || 'Sem nome'}</span>
    </span>
);

const numberFrom = (...values) => {
    const value = values.find(item => item !== undefined && item !== null);
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeTemplate = (source = {}, fallback = {}) => ({
    ...fallback,
    ...source,
    id: source.id ?? source.mass_alert_template_id ?? fallback.id,
    uuid: source.uuid ?? fallback.uuid,
    code: source.code ?? fallback.code ?? '',
    name: source.name ?? fallback.name ?? '',
    object_color_id: source.object_color_id ?? source.objectColorId ?? fallback.object_color_id ?? null,
    object_icon_id: source.object_icon_id ?? source.objectIconId ?? fallback.object_icon_id ?? null,
    default_subject: source.default_subject ?? source.defaultSubject ?? source.subject ?? fallback.default_subject ?? '',
    default_message: source.default_message ?? source.defaultMessage ?? source.message ?? fallback.default_message ?? '',
    order_no: numberFrom(source.order_no, source.orderNo, fallback.order_no),
    status: Number(source.status ?? fallback.status ?? 0),
});

const normalizeTemplates = (payload) => {
    const templates = collectionPayload(payload, 'templates');
    const massAlertTemplates = templates.length ? templates : collectionPayload(payload, 'mass_alert_templates');
    return massAlertTemplates
        .map((template, index) => normalizeTemplate(template, {id: index + 1}))
        .sort((a, b) => Number(a.order_no) - Number(b.order_no) || String(a.name).localeCompare(String(b.name)));
};

const templatePayload = (form) => ({
    code: form.code.trim(),
    name: form.name.trim(),
    object_color_id: form.object_color_id ? Number(form.object_color_id) : null,
    object_icon_id: form.object_icon_id ? Number(form.object_icon_id) : null,
    default_subject: form.default_subject.trim() || null,
    default_message: form.default_message.trim() || null,
    order_no: Number(form.order_no) || 0,
    status: Number(form.status),
});

export const MassAlertTemplatesPage = () => {
    const {can} = usePermissions();

    const [templates, setTemplates] = useState([]);
    const [colors, setColors] = useState([]);
    const [icons, setIcons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const canReadMassAlerts = can(PERMISSIONS.MASS_ALERT_READ);
    const canCreateMassAlerts = can(PERMISSIONS.MASS_ALERT_CREATE);
    const canUpdateMassAlerts = can(PERMISSIONS.MASS_ALERT_UPDATE);
    const canDeleteMassAlerts = can(PERMISSIONS.MASS_ALERT_DELETE);
    const canUseMassAlertActions = canUpdateMassAlerts || canDeleteMassAlerts;

    useEffect(() => {
        if (!canReadMassAlerts) {
            setTemplates([]);
            setColors([]);
            setIcons([]);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        const loadData = async () => {
            setLoading(true);
            setError('');

            try {
                const [templatesResponse, colorsResponse, iconsResponse] = await Promise.all([
                    api.get(massAlertTemplateEndpoints.list),
                    api.get(objectColorEndpoints.list).catch(() => ({data: []})),
                    api.get(objectIconEndpoints.list).catch(() => ({data: []})),
                ]);

                if (!cancelled) {
                    setTemplates(normalizeTemplates(templatesResponse.data));
                    setColors(collectionPayload(colorsResponse.data, 'colors').map(normalizeOption).filter(option => option.id));
                    setIcons(collectionPayload(iconsResponse.data, 'icons').map(normalizeOption).filter(option => option.id));
                }
            } catch (requestError) {
                if (!cancelled) {
                    setTemplates([]);
                    setError(getApiErrorMessage(requestError, 'Nao foi possivel carregar os modelos de alertas em massa.'));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadData();

        return () => {
            cancelled = true;
        };
    }, [canReadMassAlerts]);

    const colorById = useMemo(() => new Map(colors.map(color => [String(color.id), color])), [colors]);
    const iconById = useMemo(() => new Map(icons.map(icon => [String(icon.id), icon])), [icons]);

    const filteredTemplates = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return templates;
        return templates.filter(template => [
            template.code,
            template.name,
            template.default_subject,
            template.default_message,
            template.uuid,
        ].filter(Boolean).some(value => String(value).toLowerCase().includes(q)));
    }, [templates, search]);

    const openCreate = () => {
        if (!canCreateMassAlerts) {
            toast.error('Sem permissao para criar modelos de alertas em massa.');
            return;
        }

        setEditingTemplate(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const openEdit = (template) => {
        if (!canUpdateMassAlerts) {
            toast.error('Sem permissao para editar modelos de alertas em massa.');
            return;
        }

        setEditingTemplate(template);
        setForm({
            code: template.code || '',
            name: template.name || '',
            object_color_id: template.object_color_id ? String(template.object_color_id) : '',
            object_icon_id: template.object_icon_id ? String(template.object_icon_id) : '',
            default_subject: template.default_subject || '',
            default_message: template.default_message || '',
            order_no: String(template.order_no ?? 0),
            status: String(template.status ?? 0),
        });
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (editingTemplate && !canUpdateMassAlerts) {
            toast.error('Sem permissao para editar modelos de alertas em massa.');
            return;
        }
        if (!editingTemplate && !canCreateMassAlerts) {
            toast.error('Sem permissao para criar modelos de alertas em massa.');
            return;
        }
        if (!form.code.trim()) {
            toast.error('Codigo e obrigatorio.');
            return;
        }
        if (!form.name.trim()) {
            toast.error('Nome e obrigatorio.');
            return;
        }
        if (!form.object_icon_id) {
            toast.error('Icone e obrigatorio.');
            return;
        }
        if (!form.object_color_id) {
            toast.error('Cor e obrigatoria.');
            return;
        }

        setSubmitting(true);
        try {
            if (editingTemplate) {
                const response = await api.post(massAlertTemplateEndpoints.update, {
                    id: editingTemplate.id,
                    uuid: editingTemplate.uuid,
                    ...templatePayload(form),
                });
                const updated = normalizeTemplate(response.data?.data ?? response.data?.template ?? response.data?.mass_alert_template ?? response.data, {
                    ...editingTemplate,
                    ...templatePayload(form),
                });
                setTemplates(prev => prev.map(template => template.id === editingTemplate.id ? updated : template));
                toast.success(`Modelo "${updated.name}" atualizado.`);
            } else {
                const response = await api.post(massAlertTemplateEndpoints.create, templatePayload(form));
                const created = normalizeTemplate(response.data?.data ?? response.data?.template ?? response.data?.mass_alert_template ?? response.data, {
                    id: Date.now(),
                    ...templatePayload(form),
                });
                setTemplates(prev => [created, ...prev]);
                toast.success(`Modelo "${created.name}" criado.`);
            }
            setDialogOpen(false);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel guardar o modelo de alerta.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (template) => {
        if (!canDeleteMassAlerts) {
            toast.error('Sem permissao para eliminar modelos de alertas em massa.');
            return;
        }

        if (!window.confirm(`Eliminar modelo "${template.name}"?`)) return;

        setSubmitting(true);
        try {
            await api.post(massAlertTemplateEndpoints.delete, {
                id: template.id,
                uuid: template.uuid,
            });
            setTemplates(prev => prev.filter(item => item.id !== template.id));
            toast.success(`Modelo "${template.name}" removido.`);
        } catch (requestError) {
            toast.error(getApiErrorMessage(requestError, 'Nao foi possivel remover o modelo de alerta.'));
        } finally {
            setSubmitting(false);
        }
    };

    if (!canReadMassAlerts) {
        return (
            <Layout>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <Card className="p-8 text-center max-w-md">
                        <div className="w-14 h-14 rounded-2xl bg-red-100 mx-auto mb-4 flex items-center justify-center">
                            <ShieldOff className="text-red-600" size={28}/>
                        </div>
                        <h1 className="text-xl font-secondary font-bold text-slate-900">Acesso Negado</h1>
                        <p className="text-sm text-slate-600 mt-2">
                            Nao tem permissao para consultar modelos de alertas em massa.
                        </p>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Modelos de Alertas em Massa</h1>
                        <p className="text-slate-600 font-primary mt-1">Gerir modelos usados para criar alertas em massa.</p>
                    </div>
                    {canCreateMassAlerts && (
                        <Button onClick={openCreate} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="create-mass-alert-template">
                            <Plus size={18} className="mr-2"/>
                            Novo Modelo
                        </Button>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Total</p>
                            <Megaphone size={18} className="text-blue-600"/>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{templates.length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Ativos</p>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0">status 1</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{templates.filter(template => Number(template.status) === 1).length}</p>
                    </Card>
                    <Card className="p-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-slate-600 font-medium">Inativos</p>
                            <Badge className="bg-slate-100 text-slate-700 border-0">status 0</Badge>
                        </div>
                        <p className="text-2xl font-secondary font-bold text-slate-900 mt-1">{templates.filter(template => Number(template.status) !== 1).length}</p>
                    </Card>
                </div>

                <div className="relative max-w-md">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <Input className="pl-9" value={search} onChange={event => setSearch(event.target.value)} placeholder="Pesquisar por codigo, nome, assunto ..." data-testid="mass-alert-template-search"/>
                </div>

                <Card className="overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Modelo</TableHead>
                                <TableHead>Assunto</TableHead>
                                <TableHead>Cor</TableHead>
                                <TableHead>Icone</TableHead>
                                <TableHead>Ordem</TableHead>
                                <TableHead>Estado</TableHead>
                                {canUseMassAlertActions && <TableHead className="text-right">Acoes</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(loading || error || filteredTemplates.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={canUseMassAlertActions ? 7 : 6} className="text-center text-sm text-slate-400 italic py-10">
                                        {loading ? 'A carregar modelos...' : error || 'Sem modelos para mostrar.'}
                                    </TableCell>
                                </TableRow>
                            ) : filteredTemplates.map(template => {
                                const color = colorById.get(String(template.object_color_id));
                                const icon = iconById.get(String(template.object_icon_id));
                                return (
                                    <TableRow key={template.id} data-testid={`mass-alert-template-row-${template.id}`}>
                                        <TableCell>
                                            <p className="font-medium text-slate-900">{template.name}</p>
                                            <p className="text-xs text-slate-500 font-mono">{template.code || '-'}</p>
                                        </TableCell>
                                        <TableCell>
                                            <p className="text-sm text-slate-900 line-clamp-1">{template.default_subject || '-'}</p>
                                            {template.default_message && <p className="text-xs text-slate-500 line-clamp-1">{template.default_message}</p>}
                                        </TableCell>
                                        <TableCell>
                                            {color ? <ColorOption color={color}/> : <span className="text-xs text-slate-400 italic">Sem cor</span>}
                                        </TableCell>
                                        <TableCell>
                                            {icon ? <IconOption icon={icon}/> : <span className="text-xs text-slate-400 italic">Sem icone</span>}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-600">{template.order_no}</TableCell>
                                        <TableCell>
                                            <Badge className={Number(template.status) === 1 ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-100 text-slate-700 border-0'}>
                                                {Number(template.status) === 1 ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </TableCell>
                                        {canUseMassAlertActions && (
                                            <TableCell className="text-right">
                                                {canUpdateMassAlerts && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(template)} disabled={submitting} data-testid={`edit-mass-alert-template-${template.id}`}>
                                                        <Edit2 size={14} className="text-slate-600"/>
                                                    </Button>
                                                )}
                                                {canDeleteMassAlerts && (
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDelete(template)} disabled={submitting} data-testid={`delete-mass-alert-template-${template.id}`}>
                                                        <Trash2 size={14} className="text-red-500"/>
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>

                <Dialog
                    open={dialogOpen && (editingTemplate ? canUpdateMassAlerts : canCreateMassAlerts)}
                    onOpenChange={(open) => setDialogOpen(open && (editingTemplate ? canUpdateMassAlerts : canCreateMassAlerts))}
                >
                    <DialogContent className="max-w-2xl" data-testid="mass-alert-template-dialog">
                        <DialogHeader>
                            <DialogTitle>{editingTemplate ? 'Editar Modelo de Alerta' : 'Novo Modelo de Alerta'}</DialogTitle>
                            <DialogDescription>Configurar codigo, nome, iconografia, conteudo predefinido, ordem e estado.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Codigo *</Label>
                                    <Input value={form.code} onChange={event => setForm(prev => ({...prev, code: event.target.value}))} data-testid="mass-alert-template-code"/>
                                </div>
                                <div className="space-y-2">
                                    <Label>Nome *</Label>
                                    <Input value={form.name} onChange={event => setForm(prev => ({...prev, name: event.target.value}))} data-testid="mass-alert-template-name"/>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Palette size={13}/> Cor *</Label>
                                    <Select value={form.object_color_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_color_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="mass-alert-template-color">
                                            <SelectValue>
                                                {form.object_color_id ? <ColorOption color={colorById.get(String(form.object_color_id))}/> : 'Selecionar cor'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Selecionar cor</SelectItem>
                                            {colors.map(color => (
                                                <SelectItem key={color.id} value={String(color.id)}>
                                                    <ColorOption color={color}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1"><Shapes size={13}/> Icone *</Label>
                                    <Select value={form.object_icon_id || 'none'} onValueChange={value => setForm(prev => ({...prev, object_icon_id: value === 'none' ? '' : value}))}>
                                        <SelectTrigger data-testid="mass-alert-template-icon">
                                            <SelectValue>
                                                {form.object_icon_id ? <IconOption icon={iconById.get(String(form.object_icon_id))}/> : 'Selecionar icone'}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Selecionar icone</SelectItem>
                                            {icons.map(icon => (
                                                <SelectItem key={icon.id} value={String(icon.id)}>
                                                    <IconOption icon={icon}/>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Assunto predefinido</Label>
                                <Input value={form.default_subject} onChange={event => setForm(prev => ({...prev, default_subject: event.target.value}))} data-testid="mass-alert-template-subject"/>
                            </div>
                            <div className="space-y-2">
                                <Label>Mensagem predefinida</Label>
                                <Textarea rows={5} value={form.default_message} onChange={event => setForm(prev => ({...prev, default_message: event.target.value}))} data-testid="mass-alert-template-message"/>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label>Ordem</Label>
                                    <Input type="number" min="0" value={form.order_no} onChange={event => setForm(prev => ({...prev, order_no: event.target.value}))} data-testid="mass-alert-template-order"/>
                                </div>
                                <div className="space-y-2">
                                    <Label>Estado</Label>
                                    <Select value={form.status} onValueChange={value => setForm(prev => ({...prev, status: value}))}>
                                        <SelectTrigger data-testid="mass-alert-template-status"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="1">Ativo</SelectItem>
                                            <SelectItem value="0">Inativo</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>Cancelar</Button>
                            <Button onClick={handleSubmit} disabled={submitting || (editingTemplate ? !canUpdateMassAlerts : !canCreateMassAlerts)} className="bg-blue-600 hover:bg-blue-700" data-testid="mass-alert-template-submit">
                                {submitting ? 'A guardar...' : editingTemplate ? 'Guardar' : 'Criar Modelo'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
